import { timezoneStore } from "@/api/hooks/useConfig";
import { BuildingSelector } from "@/components/v2/BuildingSelector";
import { DatePicker } from "@/components/v2/DatePicker";
import { type PresetKey } from "@/components/v2/DatePicker/constants";
import {
  getComparisonTimeFilter,
  getTimeFilter,
} from "@/components/v2/DatePicker/helpers";
import { PdfPreviewButton } from "@/components/v2/PdfPreview";

import { useCallback, useMemo, useState } from "react";
import { DateRange } from "react-day-picker";

import { PortfolioBarCharts } from "./components/barCharts";
import { BuildingsListPortfolioPage } from "./components/buildingsList";
import { KPINumbersPortfolioPage } from "./components/kpiNumbers";
import { PortfolioHeader } from "./components/portfolioHeader";
import { PortfolioTrendView } from "./components/portfolioTrend";
import { PortfolioPageContext } from "./context";

export const PortfolioV2 = () => {
  const { timezone } = timezoneStore();
  const [dateRange, setDateRange] = useState<DateRange>();
  const [datePreset, setDatePreset] = useState<PresetKey>();
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<
    string[] | null
  >(null);

  const onDateAndPresetChange = useCallback(
    (range: DateRange, preset: PresetKey) => {
      setDateRange(range);
      setDatePreset(preset);
    },
    [],
  );

  const timeFilter = useMemo(() => getTimeFilter(dateRange), [dateRange]);

  const contextValue = useMemo(
    () => ({
      dateRange,
      datePreset,
      timeFilter,
      comparisonTimeFilter: getComparisonTimeFilter({
        datePreset,
        dateRange,
        timezone,
      }),
      selectedBuildingIds,
    }),
    [dateRange, datePreset, timezone, timeFilter, selectedBuildingIds],
  );

  const dataView = useMemo(
    () => (
      <PortfolioPageContext.Provider value={contextValue}>
        <>
          <div className="mt-6">
            <KPINumbersPortfolioPage />
          </div>

          <div className="mt-6">
            <BuildingsListPortfolioPage />
          </div>

          <div className="mt-6">
            <PortfolioTrendView />
          </div>

          <PortfolioBarCharts />
        </>
      </PortfolioPageContext.Provider>
    ),
    [contextValue],
  );

  const pdfView = useMemo(
    () => (
      <div className="h-full min-h-[660px] w-full px-[33px] py-[44px]">
        <div className="flex flex-col items-start gap-4 lg:flex-row lg:items-center lg:justify-between">
          <PortfolioHeader timeFilter={timeFilter} />
          <div className="flex items-center gap-3">
            <BuildingSelector
              timeFilter={timeFilter}
              value={selectedBuildingIds}
              onChange={setSelectedBuildingIds}
            />
            <DatePicker
              value={dateRange}
              presetValue={datePreset}
              onChange={onDateAndPresetChange}
            />
          </div>
        </div>
        {dataView}
      </div>
    ),
    [
      onDateAndPresetChange,
      dataView,
      dateRange,
      timeFilter,
      selectedBuildingIds,
      datePreset,
    ],
  );

  const view = useMemo(
    () => (
      <div className="h-full min-h-[660px] w-full px-[33px] py-[44px]">
        <div className="flex items-center justify-between">
          <PortfolioHeader timeFilter={timeFilter} />
          <div className="flex items-center gap-3">
            <BuildingSelector
              timeFilter={timeFilter}
              value={selectedBuildingIds}
              onChange={setSelectedBuildingIds}
            />
            <DatePicker
              value={dateRange}
              presetValue={datePreset}
              onChange={onDateAndPresetChange}
            />
            <PdfPreviewButton
              exportFileNameKey="portfolioPage:exportFileName"
              buttonTextKey="portfolioPage:previewButtonText"
              pdfView={pdfView}
            />
          </div>
        </div>
        {dataView}
      </div>
    ),
    [
      onDateAndPresetChange,
      dataView,
      dateRange,
      pdfView,
      timeFilter,
      selectedBuildingIds,
      datePreset,
    ],
  );

  return view;
};
